import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FormControl, useTheme } from "@mui/material";
import classNames from "classnames";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import { default as MaterialTextField, TextFieldProps as MaterialTextFieldProps } from "@mui/material/TextField";
import { TextFieldStyles } from "./TextField"; // shared styles with textfield

type Unit =
  | "weeks"
  | "days"
  | "hours"
  | "minutes"
  | "seconds"
  //
  | "week"
  | "day"
  | "hour"
  | "minute"
  | "second"
  //
  | "wks"
  | "dys"
  | "hrs"
  | "mins"
  | "secs"
  //
  | "wk"
  | "dy"
  | "hr"
  | "min"
  | "sec"
  //
  | "w"
  | "d"
  | "h"
  | "m"
  | "s";

interface IDurationProps {
  className?: string;
  meta?: Partial<FieldMetaProps<any>>;
  onChange?: (milliseconds: number) => void;
  value: number;
  id?: string; // Override use of input name for id assigned.
}

export type IDurationFieldProps = IDurationProps & MaterialTextFieldProps;

/**
 * Accepts millisecond value and converts to string matching required input.
 * @param milliseconds - number
 * @returns - formatted string
 */
export const formatDuration = (milliseconds: number): string => {
  const units = [
    { label: "months", value: 30 * 24 * 60 * 60 * 1000 },
    { label: "weeks", value: 7 * 24 * 60 * 60 * 1000 },
    { label: "days", value: 24 * 60 * 60 * 1000 },
    { label: "hours", value: 60 * 60 * 1000 },
    { label: "minutes", value: 60 * 1000 },
    { label: "seconds", value: 1000 },
  ];
  let remaining = Math.abs(milliseconds); // Always make sure this value is positive.
  let result = "";
  for (const unit of units) {
    if (remaining >= unit.value) {
      const count = Math.floor(remaining / unit.value);
      remaining -= count * unit.value;
      result += `${count}${unit.label.slice(0, 1)} `;
    }
  }
  return result.trim();
};

/**
 * Accepts a formatted string for duration using acceptable units and returns millisecond count.
 * @param durationString -
 * @returns number
 */
const parseDuration = (durationString: string): number => {
  const durationRegex = /(\d+)(\w+)/g;
  const matches = durationString.matchAll(durationRegex);
  const parsed: Map<Unit, number> = new Map();
  for (const match of matches) {
    const value = Number(match[1]);
    const unit = match[2] as Unit;
    parsed.set(unit, (parsed.get(unit) || 0) + value);
  }
  let milliseconds = 0;
  parsed.forEach((value, unit) => {
    switch (unit) {
      case "weeks":
      case "week":
      case "wks":
      case "wk":
      case "w":
        milliseconds += value * 7 * 24 * 60 * 60 * 1000;
        break;
      case "days":
      case "day":
      case "dys":
      case "dy":
      case "d":
        milliseconds += value * 24 * 60 * 60 * 1000;
        break;
      case "hours":
      case "hour":
      case "hrs":
      case "hr":
      case "h":
        milliseconds += value * 60 * 60 * 1000;
        break;
      case "minutes":
      case "minute":
      case "mins":
      case "min":
      case "m":
        milliseconds += value * 60 * 1000;
        break;
      case "seconds":
      case "second":
      case "secs":
      case "sec":
      case "s":
        milliseconds += value * 1000;
        break;
      default:
        break;
    }
  });
  return milliseconds;
};

export const DurationField = (props: IDurationFieldProps): JSX.Element => {
  const { label, name, onFocus, onBlur, onChange, value, InputProps, inputProps, className, disabled, meta, id } =
    props;

  const [durationString, setDurationString] = useState("");
  const [outputAsNegative, setOutputAsNegative] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    const durationString = typeof value === "number" && value !== 0 ? formatDuration(value) : ""; // Always assume absolute values.
    setDurationString(durationString);
    setOutputAsNegative(typeof value === "number" && value < 0);
  }, [value]);

  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const handleKeyPress = useCallback(
    (event: any): void => {
      if (event.key === "Enter") {
        event.stopPropagation();
        //@ts-ignore
        const updatedDurationString = event?.target?.value;
        const updatedMilliseconds = parseDuration(updatedDurationString);
        // update the displayed string (remove any misunderstood strings);
        setDurationString(formatDuration(updatedMilliseconds));
        if (onChange && typeof onChange === "function") {
          onChange(updatedMilliseconds > 0 && outputAsNegative ? -updatedMilliseconds : updatedMilliseconds);
        }
      }
    },
    [onChange, outputAsNegative]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setIsFocused(true);
      // Uses props onfocus (as formik doesn't supply one)
      if (onFocus && typeof onFocus === "function") {
        onFocus(e);
      }
    },
    [onFocus]
  );

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = useCallback(
    (e: React.FocusEvent<Element>): void => {
      e.persist();
      setIsFocused(false);
      const updatedMilliseconds = parseDuration(durationString);
      // Update the external value on blur if it has been changed.
      if (updatedMilliseconds !== value && onChange && typeof onChange === "function") {
        onChange(updatedMilliseconds > 0 && outputAsNegative ? -updatedMilliseconds : updatedMilliseconds);
      }
      setTimeout(() => {
        if (onBlur && typeof onBlur === "function") {
          onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
        }
      }, 1);
    },
    [durationString, value, onChange, onBlur, outputAsNegative]
  );

  const hasError = meta?.error ? true : false;
  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error",
        !label && "hideMuiLabel"
      )}
      sx={(theme) => TextFieldStyles(theme, null).root}
      error={hasError}
    >
      <MaterialTextField
        {...props}
        className="input_control"
        inputProps={{ id: id ? id : name ? name + "_input" : "", ...inputProps }}
        label={!!label && label}
        disabled={disabled}
        value={durationString} // Display millisecond value in formatted duration string.
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyPress}
        onChange={(event) => {
          // Stores users internal duration string (doesn't call external onChange);
          setDurationString(event?.target?.value);
        }}
        fullWidth={props.fullWidth !== undefined ? props.fullWidth : true}
        placeholder={props.placeholder}
        InputProps={{
          // id: name,
          autoComplete: "off",
          autoCorrect: "off",
          autoCapitalize: "off",
          spellCheck: "false",
          classes: { root: classNames("input-base") },
          ...InputProps,
        }}
      />
      <FieldError meta={meta} />
    </FormControl>
  );
};
